import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { BiX } from "react-icons/bi";

import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";

/**
 * Companion modal for the welcome-series in-app notifications (sonder
 * welcome-series-v1). Content comes entirely from the Novu message the
 * orchestrator selected (subject/body/primaryAction); dismiss or CTA marks the
 * message read server-side so it never pops again on any device.
 */
const WelcomeSeriesModal = () => {
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const history = useHistory();

  if (allstarModalState.isOpen !== ModalType.WelcomeSeries) return <></>;

  const { title, body, ctaLabel, ctaUrl, step, imageUrl } =
    allstarModalState.data;
  const { markRead } = allstarModalState.functions;

  const handleClose = () => {
    markRead?.();
    setAllstarModalState({
      data: {},
      isOpen: ModalType.None,
      functions: {},
    });
  };

  const handleCta = () => {
    analytics.track("Promotion - Button Click", {
      type: "welcomeSeriesModal",
      promotionName: `welcome-${step}`,
    });

    const url: string = ctaUrl || "";
    // sonder sends absolute allstar.gg URLs (with utm_source=in-app); route
    // same-site ones through the SPA so the utm-carrying page load is tracked.
    const path = url.replace(/^https?:\/\/(www\.)?allstar\.gg/, "");
    if (path && path !== url) {
      history.push(path);
    } else if (url) {
      window.open(url, "_blank");
    }
    handleClose();
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalFrame onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <BiX size={24} />
        </CloseButton>

        <ModalContainer>
          <ModalContent>
            {imageUrl && <HeroImage src={imageUrl} alt="" />}
            <TextBlock>
              <Title>{title}</Title>
              <Description>{body}</Description>
            </TextBlock>

            {ctaLabel && (
              <ActionButton onClick={handleCta}>{ctaLabel}</ActionButton>
            )}
          </ModalContent>
        </ModalContainer>
      </ModalFrame>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

// Frame owns sizing + the floating close button; the container scrolls. The X
// can't live inside the scrollable box — overflow: auto would clip it at the
// corner it overhangs.
const ModalFrame = styled(Box)`
  max-width: 840px;
  position: relative;
  width: 90%;
`;

const ModalContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 16px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 48px;
`;

const CloseButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border: none;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  position: absolute;
  right: -16px;
  top: -16px;
  transition: background-color 0.2s ease;
  width: 40px;

  &:hover {
    background: ${({ theme }) => theme.colors.ned};
  }
`;

const ModalContent = styled(Flex)`
  flex-direction: column;
  gap: 28px;
`;

const HeroImage = styled.img`
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  object-fit: cover;
  width: 100%;
`;

const TextBlock = styled(Flex)`
  flex-direction: column;
  gap: 12px;
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 20px;
  font-weight: 500;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.spectrum};
  border: none;
  border-radius: 999px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 18px;
  font-weight: 600;
  height: 56px;
  justify-content: center;
  transition: opacity 0.2s ease;
  width: 100%;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

export { WelcomeSeriesModal };
